import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPost } from '../../../../api/playgroundServices';
import { searchHighlights } from '../../../../api/highlightServices';
import { HighlightData } from '../../../common/ContentEditable/ContentEditable';
import './NewPostInputModal.css';
import ContentEditable from '../../../common/ContentEditable/ContentEditable';
import ModalHeader from './ModalHeader';
import ModalFooter from './ModalFooter';
import VIToast from '../../../common/VIToast/VIToast';
import Camera from '../../../common/Camera/Camera';
import GiphyModal from '../../../common/GiphyModal/GiphyModal';
import { UserType } from '../../../../types/userType';
import { PostProps, PostMedia } from '../../../../types/postType';
import { HighlightItem } from '../../../../api/highlightServices';
import {  processMediaFiles, processCameraCapture } from '../../../../services/mediaServices';
import UploadedMediaManager from './MediaManager';
import icon_newpost_images from '../../../../assets/icons/icon_newpost_images.svg';

interface NewPostInputModalProps {
  isOpen: boolean;
  onClose: () => void;
  username: string;
  avatarSrc: string;
  currentUser: UserType;
}
interface ModalHeaderProps {
  onClose: () => void;
}

const NewPostInputModal: React.FC<NewPostInputModalProps> = ({
  isOpen, onClose, username, avatarSrc, currentUser
}) => {
  // Basic content state
  const [text, setText] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [highlights, setHighlights] = useState<HighlightData[]>([]);

  // Media state
  const [mediaList, setMediaList] = useState<PostMedia[]>([]);
  const [selectedGif, setSelectedGif] = useState<string | null>(null);

  // UI state
  const [isPosting, setIsPosting] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [showCamera, setShowCamera] = useState(false);
  const [showGiphyModal, setShowGiphyModal] = useState(false);

  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null);
  const contentEditableRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Add a state to track if we're on iOS
  const [isIOS, setIsIOS] = useState(false);
  
  // Detect iOS on component mount
  useEffect(() => {
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera || '';
    const isIOSDevice = /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
    setIsIOS(isIOSDevice);
    console.log("Device detection - iOS:", isIOSDevice);
  }, []);

  // Cleanup function for URL objects to prevent memory leaks
  const revokeMediaURLs = useCallback(() => {
    mediaList.forEach(media => {
      if (media.url.startsWith('blob:')) {
        URL.revokeObjectURL(media.url);
      }
    });
  }, [mediaList]);

  // Cleanup URLs when component unmounts
  useEffect(() => {
    return () => revokeMediaURLs();
  }, [revokeMediaURLs]);

  const resetStates = useCallback(() => {
    setText('');
    setHtmlContent('');
    setHighlights([]);
    setMediaList([]);
    setSelectedGif(null);
    setIsPosting(false);
    setToastMessage(null);
    setShowCamera(false);
    setShowGiphyModal(false);
    
    if (contentEditableRef.current) {
      contentEditableRef.current.innerHTML = '';
    }
  }, []);

  const handleClose = useCallback(() => {
    resetStates();
    onClose();
  }, [onClose, resetStates]);

  const handleTextChange = useCallback((newText: string) => {
    setText(newText);
  }, []);

  const handleHighlightSearch = async (query: string) => {
    try {
      const results = await searchHighlights(query);
      return results;
    } catch (error) {
      console.error('Error searching users:', error);
      return [];
    }
  };

  const handlePost = async () => {
    if (text.trim() === '' && mediaList.length === 0 && !selectedGif) {
      setToastMessage('Please add some content to your post.');
      return;
    }

    setIsPosting(true);

    try {
      const allMedia: PostMedia[] = [
        ...mediaList,
        ...(selectedGif ? [{
          url: selectedGif,
          media_type: 'gif' as const,
          width: 0,
          height: 0
        }] : [])
      ];

      const formattedHighlights: HighlightItem[] = highlights.map((highlight, index) => ({
        highlight_text: highlight.highlight_text,
        profile_picture: '',
        highlight_type: highlight.highlight_type,
        mention_type: highlight.mention_type,
        user_id: highlight.data?.user_id,
        tag_id: highlight.data?.tag_id,
        position: index
      }));

      const postData: PostProps = {
        content: text,
        mediaList: allMedia,
        highlights: formattedHighlights,
        id: 0,
        slug: '',
        user: currentUser,
        timestamp: new Date().toISOString(),
        postType: allMedia.length > 0 ? 'media' : 'text',
        currentUser,
        currentUserId: currentUser.id,
        isNewPost: true,
        isSelfPost: true,
        numLikes: 0,
        numReplies: 0,
        likedStatus: false,
        onPostDeleted: () => {},
        onReplyClick: () => {},
      };

      await createPost(postData);
      onClose();
    } catch (error) {
      console.error('Error creating post:', error);
      setToastMessage('Failed to create post. Please try again.');
    } finally {
      setIsPosting(false);
      // Reset all states regardless of success or failure
      resetStates();
      
      // Reset ContentEditable content and state
      if (contentEditableRef.current) {
        contentEditableRef.current.innerHTML = '';
        setHtmlContent('');
        setText('');
        setHighlights([]);
      }
    }
  };

  // Handle file selection
  const handleFileSelection = async (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log("=== FILE SELECTION STARTED ===");
    console.log('File input changed', e.target.files);
    
    const target = e.target;
    if (!target.files || target.files.length === 0) {
      console.log('No files selected');
      return;
    }
    
    console.log('Files selected:', target.files.length);
    
    // Show loading toast
    setToastMessage("Processing media...");
    
    try {
      // Use the refactored service function
      const { success, errors } = await processMediaFiles(target.files);
      
      if (errors.length > 0) {
        errors.forEach(error => setToastMessage(error));
      }
      
      if (success.length > 0) {
        setMediaList(prev => [...prev, ...success]);
        
        // Load dimensions for images
        for (const media of success) {
          if (media.media_type === 'image' || media.media_type === 'gif') {
            try {
              await new Promise<void>((resolve) => {
                const img = new Image();
                img.onload = () => {
                  setMediaList(prev => 
                    prev.map(item => 
                      item.url === media.url 
                        ? {...item, width: img.width, height: img.height} 
                        : item
                    )
                  );
                  resolve();
                };
                img.onerror = () => {
                  console.error("Failed to load image dimensions");
                  resolve();
                };
                img.src = media.url;
              });
            } catch (err) {
              console.error("Error loading image dimensions:", err);
            }
          }
        }
        
        setToastMessage(null);
      }
    } catch (error) {
      console.error("Error processing files:", error);
      setToastMessage("Error uploading media. Please try again.");
    }
    
    // Reset the file input value
    if (target) {
      target.value = '';
    }
    
    console.log("=== FILE SELECTION COMPLETED ===");
  };

  // Create a custom file input component that works on all devices
  const CustomFileInput = () => {
    return (
      <label style={{
        display: 'inline-block',
        cursor: 'pointer',
        margin: 0,
        padding: 0
      }}>
        <input
          type="file"
          multiple
          accept="image/*, video/mp4, video/quicktime, video/*"
          onChange={handleFileSelection}
          style={{
            position: 'absolute',
            width: '1px',
            height: '1px',
            padding: 0,
            margin: '-1px',
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            border: 0
          }}
        />
        <img src={icon_newpost_images} alt="Upload media" />
      </label>
    );
  };

  return (
    <div className={`modal-overlay ${isOpen ? 'open' : ''}`}>
      <div className="modal-content" ref={modalRef}>
        <ModalHeader onClose={handleClose} />
        
        <div className="modal-body">
          <ContentEditable
            text={text}
            highlights={highlights}
            onChange={handleTextChange}
            onHighlightSelect={handleHighlightSearch}
            onHighlightsChange={setHighlights}
            ref={contentEditableRef}
          />

          <UploadedMediaManager
            mediaList={mediaList}
            selectedGif={selectedGif}
            onMediaListChange={setMediaList}
            onGifChange={setSelectedGif}
            onError={setToastMessage}
            maxItems={10}
          />
        </div>

        <ModalFooter
          onMediaUpload={() => {
            // This will be a no-op function since we're using a custom button
          }}
          customMediaUploadButton={<CustomFileInput />}
          onCameraCapture={() => setShowCamera(true)}
          onGifClick={() => setShowGiphyModal(true)}
          isPostButtonEnabled={!!text.trim() || mediaList.length > 0 || !!selectedGif}
          isPosting={isPosting}
          onPost={handlePost}
        />

        {toastMessage && (
          <VIToast 
            message={toastMessage} 
            onClose={() => setToastMessage(null)} 
          />
        )}

        {showCamera && (
          <Camera
            onCapture={(imageSrc: string) => {
              const byteString = atob(imageSrc.split(',')[1]);
              const mimeString = imageSrc.split(',')[0].split(':')[1].split(';')[0];
              const ab = new ArrayBuffer(byteString.length);
              const ia = new Uint8Array(ab);
              for (let i = 0; i < byteString.length; i++) {
                ia[i] = byteString.charCodeAt(i);
              }
              const file = new File([ab], 'capture.jpg', { type: mimeString });
              
              setMediaList(prev => [...prev, { 
                url: imageSrc,
                file,
                media_type: 'image',
                width: 0,
                height: 0
              }]);
              setShowCamera(false);
            }}
            onClose={() => setShowCamera(false)}
          />
        )}

        {showGiphyModal && (
          <GiphyModal
            isOpen={showGiphyModal}
            onSelect={(gifUrl) => {
              setSelectedGif(gifUrl);
              setShowGiphyModal(false);
            }}
            onClose={() => setShowGiphyModal(false)}
          />
        )}
      </div>
    </div>
  );
};

export default NewPostInputModal;